//bfsg

import * as React from 'react'
import styled from 'styled-components'
import { ReactComponent as ArrowLeft } from 'assets/back.svg'
import theme from 'theme'
import Link from 'theme/atoms/Link'
import * as t from '../types'
import { useCategoryTree } from 'modules/listing'
import useTagCategoryPaths from 'hooks/useTagCategoryPaths'
import Button from 'theme/atoms/Button'
import useTranslations from 'hooks/useTranslations'
import * as evt from '../events'
import usePageContext from 'hooks/usePageContext'

type Props = {
  item: t.CategoryOption | null
  parent: t.CategoryOption | null
}

export default function MobileCategories(props: Props) {
  const { item, parent } = props
  const categoryTree = useCategoryTree('category')
  const tc = useTagCategoryPaths()
  const t = useTranslations<'molecules-ListingCategoryTree'>()
  const context = usePageContext()

  const items = item?.data?.length ? item.data : parent?.data || []

  const isRoot = categoryTree.data.item.path === ''
  const hasBackBtn = tc.active ? !isRoot : Boolean(parent)
  let backLink = tc.active
    ? tc.get(item?.path || '')
      ? tc.get(parent?.path || '')
      : ''
    : parent?.urlPath || ''

  // console.log(tc.active, tc.get(item?.path || ''), parent?.urlPath)
  // when we want to navigate from a virtual category to a real category we cannot navigate
  // because the url won't change. Instead we use the filter mechanics
  if (typeof window !== 'undefined' && backLink === window.location.pathname)
    backLink = ''

  const filteredList = items.filter((item) => !item.isRefined)

  // when we want to navigate from a virtual category to a real category we cannot navigate
  // because the url won't change. Instead we use the filter mechanics
  if (typeof window !== 'undefined' && backLink === window.location.pathname)
    backLink = ''

  /**
   * track chip click and update category when child is a virtual category (tag-category)
   */
  const handleChipMenuClick = (opt: t.CategoryOption | null) => () => {
    if (!opt) return
    const urlPath = tc.get(opt.path) ?? opt.urlPath
    const label = urlPath.split('/').filter(Boolean).pop() ?? '-'
    evt.chipClick(label)

    if (tc.active && !tc.has(opt.path)) {
      categoryTree.setCategory(opt.path)
      evt.tagCategoryClick(
        context?.type === 'Category' ? context.dyPath : 'error'
      )
    }
  }

  /**
   * returns the href link für chips. tag-categories which do not have a
   * physical route do not have a link (undefined instead)
   */
  const getChipLink = (item: t.CategoryOption) => {
    if (tc.active && !tc.has(item.path)) return undefined
    return tc.get(item.path) || item.urlPath
  }

  return (
    <Wrapper
      className="MobileCategories"
      data-cy-ctx="molecules/ListingCategoryTree"
    >
      <div className="BackButtonContainer">
        {hasBackBtn && backLink && (
          <Link
            to={backLink}
            data-cy-state="show-back-button"
            data-cy-handle="back-button"
          >
            <ArrowLeft />
            <span className="back-btn-label">
              {parent?.name
                ? t('category_side_nav_back_link', {
                    categoryname: parent?.name
                  })
                : t('category_side_nav_back_link_fallback')}
            </span>
          </Link>
        )}
        {hasBackBtn && !backLink && (
          <button
            className="back-btn"
            data-cy-state="show-back-button"
            onClick={() => {
              categoryTree.setCategory(parent?.path || '')
              evt.tagCategoryClick(
                context?.type === 'Category' ? context.dyPath : 'error'
              )
            }}
            data-cy-handle="back-button"
          >
            <ArrowLeft />
            <span className="back-btn-label">
              {parent?.name
                ? t('category_side_nav_back_link', {
                    categoryname: parent?.name
                  })
                : t('category_side_nav_back_link_fallback')}
            </span>
          </button>
        )}
      </div>
      <div className="categories">
        {filteredList.map((item) => (
          <Button
            to={getChipLink(item)}
            key={item.path}
            variation="primary_s"
            onClick={handleChipMenuClick(item)}
            data-cy-handle="cat-option"
          >
            {item.name}
          </Button>
        ))}
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 100%;
  position: relative;

  > .BackButtonContainer {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-left: 0.625rem;

    > button,
    a > .back-btn-label {
      ${theme.ty('r-base', '700')}
      margin-left: ${theme.spacing('xxs')};
    }
  }

  > .categories {
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing('s')};
    height: 3.75rem;
    width: calc(100vw - 1.25rem);
    overflow: scroll;
    padding: ${theme.spacing('xs')} 0;
    -ms-overflow-style: none;
    scrollbar-width: none;
    white-space: nowrap;
    &::-webkit-scrollbar {
      display: none;
    }
    > button,
    a {
      background: rgb(43, 39, 31);
    }
  }
`
